import { IMAGES } from "../data/site";
import { Reveal, Label, BookButton, Marquee } from "../components/ui-kit";
import { Heart, Compass, Users, UserPlus } from "lucide-react";

const PILLARS = [
  { icon: Heart, title: "Meet you where you are", desc: "No judgment, no expectations. Whether it's day one or year ten, every session is built around you." },
  { icon: Compass, title: "Coach every rep", desc: "You'll never guess what to do. Our coaches guide you through every movement, safely and confidently." },
  { icon: Users, title: "Build real community", desc: "The people beside you become friends who celebrate your wins and keep you accountable." },
];

export default function About() {
  return (
    <>
      <section className="relative pt-40 pb-24 grain overflow-hidden" data-testid="about-hero">
        <img src={IMAGES.highfive} alt="HFC community" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Label>Our Story</Label>
          <h1 className="font-display uppercase text-white text-5xl md:text-8xl leading-[0.9] mt-6 max-w-4xl">Built for real people, not gym rats.</h1>
          <p className="mt-8 text-lg md:text-xl text-lightgrey max-w-2xl">Hybrid Fitness Collective started with a simple belief: everyday people deserve expert coaching in a place that feels welcoming — not intimidating.</p>
        </div>
      </section>

      <section className="bg-black py-24" data-testid="mission">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal><Label className="justify-center">Our Mission</Label></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-display text-3xl md:text-5xl text-white leading-tight uppercase">
              To help the people of Pictou County become <span className="text-gold">stronger, healthier and more confident</span> — one coached session at a time.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Everyday People", "Expert Coaching", "Zero Judgment", "Real Community"]} reverse />

      <section className="bg-charcoal py-24 md:py-32" data-testid="meet-emily">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-white/10">
              <img src={IMAGES.emily} alt="Emily Morton, Head Coach" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Label>Meet Your Coach</Label>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none">Emily Morton</h2>
            <p className="text-gold mt-2 uppercase tracking-wide text-sm">Founder & Head Coach</p>
            <div className="mt-6 space-y-4 text-lightgrey text-lg">
              <p>Emily grew up in Pictou County and knows firsthand how intimidating fitness can feel when you're just starting out. After years of coaching, she built HFC to be the gym she always wished existed — one where the coaching is world-class and the door is wide open to everyone.</p>
              <p>Her philosophy is simple: meet people where they are, coach every movement with care, and build a community strong enough to carry each other. No egos. No shortcuts. Just steady, honest progress.</p>
            </div>
            <div className="mt-8"><BookButton testid="about-book">Meet Emily — Book a Free Intro</BookButton></div>
          </Reveal>
        </div>
      </section>

      <section className="bg-black py-24 md:py-32" data-testid="meet-steve">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="lg:order-2">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-white/10">
              <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/ad93um5f_IMG_3961.webp" alt="Coach Steve" className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:order-1">
            <Label>Meet Your Coach</Label>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none">Steve</h2>
            <p className="text-gold mt-2 uppercase tracking-wide text-sm">Founder & Head Coach</p>
            <div className="mt-6 space-y-4 text-lightgrey text-lg">
              <p>Steve is the other half of HFC's coaching team, and the guy our members love to "torture" back in every class. His approach is equal parts high standards and good humour — he'll push you to do more than you thought you could, then have you laughing before you catch your breath.</p>
              <p>With a passion for functional strength and Hyrox-style training, Steve makes sure every session is safe, scalable, and genuinely fun. Whether it's your first class or your five-hundredth, he coaches every rep like it matters — because to him, you do.</p>
            </div>
            <div className="mt-8">
              <a
                href="https://m.me/smooresfit"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-steve-dm"
                className="group inline-flex items-center justify-center rounded-full bg-gold text-black font-bold px-8 py-4 hover:bg-white transition-colors duration-300"
              >
                Question? Shoot me a DM
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal py-24 md:py-32" data-testid="new-coaches">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>The Crew Is Growing</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none max-w-2xl">Members turned coaches.</h2>
            <p className="mt-5 text-lightgrey text-lg max-w-2xl">Nobody understands the journey like someone who's walked it. Robin and Kelsey started out right where you might be today — and now they're the ones in your corner.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {[
              {
                name: "Robin",
                role: "Coach · Member Turned Coach",
                desc: "Robin walked through our doors as a nervous beginner and never looked back. That's exactly why she's the coach you want on day one — patient, encouraging, and relentlessly in your corner. She has a gift for spotting the little wins you'd never notice yourself, and she'll make sure you leave every session standing a little taller than you walked in.",
                testid: "coach-robin",
                image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/b4ad0031223be9c9_IMG_7941.webp",
                pos: "center 30%",
              },
              {
                name: "Kelsey",
                role: "Coach · Member Turned Coach",
                desc: "Kelsey trains with the kind of quiet confidence that's downright contagious. She's living proof of what consistency builds, and she coaches with high standards wrapped in a whole lot of heart. Expect her to push you past what you thought you could do — then celebrate it louder than anyone. Tough, warm, and 100% in it with you.",
                testid: "coach-kelsey",
                image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/74212e5b592817df_IMG_7884.webp",
                pos: "center 18%",
              },
            ].map((c, i) => (
              <Reveal key={c.name} delay={i * 0.1}>
                <div className="rounded-3xl border border-white/10 bg-black/40 overflow-hidden h-full hover:border-gold/40 transition-all duration-300" data-testid={c.testid}>
                  <div className="relative aspect-[4/3] bg-charcoal flex items-center justify-center border-b border-white/10">
                    {c.image ? (
                      <img src={c.image} alt={`Coach ${c.name}`} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: c.pos || "center" }} />
                    ) : (
                      <div className="text-center px-6">
                        <span className="inline-flex w-14 h-14 rounded-full bg-gold/15 border border-gold/30 items-center justify-center text-gold mb-3"><UserPlus size={24} /></span>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/50">Photo placeholder — add {c.name}'s photo</p>
                      </div>
                    )}
                  </div>
                  <div className="p-8">
                    <h3 className="font-display text-3xl text-white uppercase">{c.name}</h3>
                    <p className="text-gold mt-1 uppercase tracking-wide text-xs">{c.role}</p>
                    <p className="mt-4 text-lightgrey">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>



      <section className="bg-black py-24 md:py-32" data-testid="philosophy">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>Coaching Philosophy</Label></Reveal>
          <Reveal delay={0.1}><h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none max-w-2xl">How we coach.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="rounded-3xl border border-white/10 bg-charcoal p-8 h-full hover:border-gold/40 hover:-translate-y-1 transition-all duration-300">
                  <span className="inline-flex w-12 h-12 rounded-xl bg-gold/15 border border-gold/30 items-center justify-center text-gold mb-6"><p.icon size={22} /></span>
                  <h3 className="font-display text-2xl text-white uppercase">{p.title}</h3>
                  <p className="mt-3 text-lightgrey">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal py-24" data-testid="community-photos">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>Our Community</Label></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {[
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/458j6jqw_IMG_8005.webp",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/ucs9n7wa_IMG_7941.webp",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/x5jugku9_IMG_7855.webp",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_hfc-stronger/artifacts/cxf15z1f_IMG_8158.webp",
            ].map((src, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 group">
                  <img src={src} alt="HFC community" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
